import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const cardVariants = cva(
  'transition-[border-color,box-shadow] duration-200 ease-out text-card-foreground',
  {
    variants: {
      variant: {
        default: 'border border-subtle bg-surface-card shadow-none',
        elevated: 'border border-subtle bg-surface-card shadow-paper hover:shadow-elevated',
        outlined: 'border border-hairline bg-transparent shadow-none',
        filled: 'border border-subtle bg-surface-nested shadow-none',
      },
      size: {
        sm: 'rounded-control',
        md: 'rounded-card',
        lg: 'rounded-elevated',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, size }), className)} {...props} />
  )
);

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-b border-subtle px-5 py-5 last:border-b-0 sm:px-6 sm:py-6 md:px-8',
        className
      )}
      {...props}
    />
  )
);

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-5 py-5 sm:px-6 sm:py-6 md:px-8', className)} {...props} />
  )
);

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-t border-subtle px-5 py-5 first:border-t-0 sm:px-6 sm:py-6 md:px-8',
        className
      )}
      {...props}
    />
  )
);

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardContent.displayName = 'CardContent';
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardContent, CardFooter, cardVariants };
